import { t } from "@web-monorepo/i18n/macro";
import { getImageSrc } from "@web-monorepo/static-assets";
import { SensitivitySlider } from "./SensitivitySlider";
import styles from "./NoiseDisplay.module.css";
import {
  FREQUENCY_BIN_MAX,
  MAX_BAR_HEIGHT,
  MIN_BAR_HEIGHT,
  useNoiseDisplay,
  type UseNoiseDisplayProps,
  type UseNoiseDisplayResult,
} from "./useNoiseDisplay.vm";

const permissionErrorLogo = getImageSrc("toolkit/src/pods/noise/assets/mic_error.png");

type NoiseDisplayProps = {
  sendNoise?: (payload: { gain: number }) => void;
  gain?: number;
  showSensitivityInput?: boolean;
  onPermissionError?: (errorName: string | null) => void;
  /**
   * Dependency injection for the view model — Storybook stories override this
   * with a stub `useVm` returning canned state, sidestepping the microphone
   * and audio APIs entirely. Production callers leave the default.
   */
  di?: {
    useVm?: (props: UseNoiseDisplayProps) => UseNoiseDisplayResult;
  };
};

export default function NoiseDisplay({
  sendNoise,
  gain,
  showSensitivityInput,
  onPermissionError,
  di,
}: NoiseDisplayProps) {
  const useVm = di?.useVm ?? useNoiseDisplay;
  const { hasPermission, frequencies, permissionErrorMessage } = useVm({ gain, onPermissionError });

  const bars = frequencies && frequencies.slice(0, Math.round(frequencies.length / 4));

  return (
    <div className={styles.display}>
      {hasPermission && (
        <div>
          <div className={styles.bars}>
            <span className={styles.fillerBar} />
            {bars &&
              bars.map((frequency, idx) => (
                <span
                  key={idx}
                  className={styles.bar}
                  style={{ height: `${Math.max(MAX_BAR_HEIGHT * (frequency / FREQUENCY_BIN_MAX), MIN_BAR_HEIGHT)}px` }}
                />
              ))}
          </div>
          {showSensitivityInput && (
            <div className={styles.sliderWrap}>
              <SensitivitySlider
                aria-label={t`Noise sensitivity range`}
                label={t`Sensitivity`}
                value={gain || 0}
                min={-125}
                max={125}
                step={1}
                onChange={(value) => sendNoise?.({ gain: value })}
                theme="light"
              />
            </div>
          )}
        </div>
      )}
      {/* hasPermission null (pending) also falls here intentionally — show the prompt card while getUserMedia is in flight */}
      {!hasPermission && (
        <div className={styles.permissionBox}>
          <img src={permissionErrorLogo} className={styles.permissionErrorLogo} alt="" />
          <div className={styles.permissionText}>
            {permissionErrorMessage || t`To use Noise Meter, please allow use of your microphone.`}
          </div>
        </div>
      )}
    </div>
  );
}
